// MySQL Sync Tab - Compact, information-dense view of all synced MySQL tables

import { Button, StatusBadge } from '../common';
import type { SyncStatus, SyncJobStats } from '../../types/api';
import { formatDate, formatDuration } from '../../utils/dateFormatters';
import styles from './MySQLSyncTab.module.css';

// Format a count with locale separators, or show dash for zero
function fmtCount(n: number | undefined): string {
  if (!n) return '0';
  return n.toLocaleString();
}

// Table row data definition
interface TableRowData {
  name: string;
  source: string;
  count: number;
}

// Database section with compact table
function DbSection({
  dbName,
  label,
  rows,
}: {
  dbName: string;
  label: string;
  rows: TableRowData[];
}) {
  return (
    <div className={styles.dbSection}>
      <div className={styles.dbHeader}>
        {dbName}
        <span className={styles.dbLabel}>{label}</span>
      </div>
      <table className={styles.syncTable}>
        <thead>
          <tr>
            <th>Table</th>
            <th>Records</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.name}>
              <td>
                <span className={styles.tableName}>{row.name}</span>
                <span className={styles.sourceTable}>{row.source}</span>
              </td>
              <td>
                <span className={row.count > 0 ? styles.countValue : styles.countZero}>
                  {fmtCount(row.count)}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Job counter cell: shows total with +created/~updated detail
function JobCounter({
  synced,
  created,
  updated,
}: {
  synced: number;
  created?: number;
  updated?: number;
}) {
  if (!synced && !created && !updated) return <span className={styles.countZero}>-</span>;
  return (
    <span className={styles.jobCounter}>
      <span className={styles.jobCounterMain}>{synced.toLocaleString()}</span>
      {(created !== undefined || updated !== undefined) && (
        <span className={styles.jobCounterDetail}>
          (+{created ?? 0}/~{updated ?? 0})
        </span>
      )}
    </span>
  );
}

// Sync status badge
function SyncStatusBadge({ status }: { status: string }) {
  const variant = status === 'completed' ? 'success' : status === 'running' ? 'info' : 'error';
  return (
    <StatusBadge variant={variant} pulse={status === 'running'}>
      {status}
    </StatusBadge>
  );
}

// Sync mode badge
function SyncModeBadge({ mode }: { mode: string }) {
  const variant = mode === 'incremental' ? 'info' : 'neutral';
  return (
    <StatusBadge variant={variant}>
      {mode}
    </StatusBadge>
  );
}

interface MySQLSyncTabProps {
  syncStatus: SyncStatus | null;
  isTriggering: boolean;
  onTriggerSync: (syncType?: string) => void;
}

export default function MySQLSyncTab({ syncStatus, isTriggering, onTriggerSync }: MySQLSyncTabProps) {
  // Build table data for each database
  const sageRows: TableRowData[] = [
    { name: 'Customers', source: 'AR_Customer', count: syncStatus?.customers.count ?? 0 },
    { name: 'Products', source: 'CI_Item', count: syncStatus?.products.count ?? 0 },
    { name: 'Salespersons', source: 'AR_Salesperson', count: syncStatus?.sage_data?.salespersons ?? 0 },
    { name: 'Sales History', source: 'mvChatBotSales', count: syncStatus?.sage_data?.sales_history ?? 0 },
    { name: 'Product Costs', source: 'product_cost_history', count: syncStatus?.sage_data?.product_cost_snapshots ?? 0 },
    { name: 'Ship-To Addresses', source: 'SO_ShipToAddress', count: syncStatus?.sage_data?.ship_to_addresses ?? 0 },
    { name: 'Item Warehouses', source: 'IM_ItemWarehouse', count: syncStatus?.sage_data?.item_warehouses ?? 0 },
  ];

  const referenceRows: TableRowData[] = [
    { name: 'Brand Cross-Refs', source: 'competitor_mappings', count: syncStatus?.reference_data?.brand_cross_references ?? 0 },
    { name: 'SDS References', source: 'safety_data_sheets', count: syncStatus?.reference_data?.safety_data_sheets ?? 0 },
    { name: 'Product Lookups', source: 'product_images_links', count: syncStatus?.reference_data?.product_lookups ?? 0 },
  ];

  const salesRows: TableRowData[] = [
    { name: 'Sales Reps', source: 'rep', count: syncStatus?.sales_app?.sales_representatives ?? 0 },
    { name: 'Sales Users', source: 'user', count: syncStatus?.sales_app?.sales_app_users ?? 0 },
    { name: 'Customer Locations', source: 'location', count: syncStatus?.sales_app?.customer_locations ?? 0 },
    { name: 'Sales Contacts', source: 'contact', count: syncStatus?.sales_app?.sales_contacts ?? 0 },
    { name: 'Quotes', source: 'quote', count: syncStatus?.sales_app?.sales_quotes ?? 0 },
    { name: 'Quote Products', source: 'quote_product', count: syncStatus?.sales_app?.quote_products ?? 0 },
    { name: 'Special Pricing', source: 'special_pricing', count: syncStatus?.sales_app?.special_pricing ?? 0 },
    { name: 'Pricing Products', source: 'special_pricing_product', count: syncStatus?.sales_app?.special_pricing_products ?? 0 },
  ];

  // Check if any tables need initial sync
  const needsSync = sageRows.some(r => r.count === 0) || salesRows.some(r => r.count === 0);

  return (
    <>
      <div className={styles.tabHeader}>
        <div>
          <p className={styles.tabDescription}>
            Synced from 3 MySQL databases: Sage ERP, reference data, and sales portal.
          </p>
          {syncStatus?.customers.last_synced_at && (
            <p className={styles.lastSyncInfo}>
              Last sync: {formatDate(syncStatus.customers.last_synced_at)}
            </p>
          )}
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onTriggerSync('incremental')}
            isLoading={isTriggering}
          >
            Incremental Sync
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => onTriggerSync('full')}
            isLoading={isTriggering}
          >
            Full Sync
          </Button>
        </div>
      </div>

      <DbSection
        dbName="chemmas_iq_sage"
        label="Sage ERP"
        rows={sageRows}
      />
      <DbSection
        dbName="chemmas_reference"
        label="Reference Data"
        rows={referenceRows}
      />
      <DbSection
        dbName="chemmas_sales"
        label="Sales Portal"
        rows={salesRows}
      />

      {needsSync && (
        <div className={styles.needsSyncCallout}>
          Some tables show 0 records. Run "Trigger Full Sync" to populate all data.
        </div>
      )}

      {/* Recent Sync Jobs */}
      <div className={styles.jobsSection}>
        <div className={styles.jobsHeader}>Recent Sync Jobs</div>
        <div style={{ overflowX: 'auto' }}>
          <table className={styles.jobsTable}>
            <thead>
              <tr>
                <th>Status</th>
                <th>Type</th>
                <th>Mode</th>
                <th>Started</th>
                <th>Duration</th>
                <th>Customers</th>
                <th>Products</th>
                <th>Ship-To</th>
                <th>Contacts</th>
                <th>Errors</th>
              </tr>
            </thead>
            <tbody>
              {syncStatus?.recent_jobs && syncStatus.recent_jobs.length > 0 ? (
                syncStatus.recent_jobs.map((job: SyncJobStats) => (
                  <tr key={job.id}>
                    <td><SyncStatusBadge status={job.status} /></td>
                    <td>{job.sync_type}</td>
                    <td><SyncModeBadge mode={job.sync_mode || 'full'} /></td>
                    <td>{formatDate(job.started_at)}</td>
                    <td>{formatDuration(job.started_at, job.completed_at)}</td>
                    <td>
                      <JobCounter
                        synced={job.customers_synced}
                        created={job.customers_created}
                        updated={job.customers_updated}
                      />
                    </td>
                    <td>
                      <JobCounter
                        synced={job.products_synced}
                        created={job.products_created}
                        updated={job.products_updated}
                      />
                    </td>
                    <td>
                      <JobCounter
                        synced={job.ship_to_addresses_synced}
                        created={job.ship_to_addresses_created}
                        updated={job.ship_to_addresses_updated}
                      />
                    </td>
                    <td>
                      <JobCounter
                        synced={job.contacts_synced}
                        created={job.contacts_created}
                        updated={job.contacts_updated}
                      />
                    </td>
                    <td>
                      {job.error_message ? (
                        <span className={styles.errorBadge} title={job.error_message}>
                          <span className={styles.errorDot} />
                          {job.error_message.length > 30
                            ? `${job.error_message.substring(0, 30)}...`
                            : job.error_message}
                        </span>
                      ) : (
                        <StatusBadge variant="success">None</StatusBadge>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={10} className={styles.emptyRow}>
                    No sync jobs found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
